import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector, useCan } from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import { money, fmtCountdown } from "@/utils/format";
import { fmtShortDateTime, fmtClock } from "@/utils/chartTime";
import { fetchTradingHistory } from "@/state/slices/tradingSlice";
import { pushToast } from "@/state/slices/notificationsSlice";
import { api, ApiError } from "@/api/client";
import type { TradingDeal } from "@/api/types";
import styles from "./PositionsPanel.module.css";

/** Parse a price input; "" → null (= keep / clear depending on caller). */
function parsePrice(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return isFinite(n) && n >= 0 ? n : null;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

type Tab = "positions" | "orders" | "history";

/** Round-trip view of a closed position. the trading server returns deals (one per
 *  fill) — entry "in" + exit "out" share a position_id. We group by
 *  position_id to reconstruct the round-trip with combined P&L.
 *  Exported so the TradeJournal drawer can render the same Closed
 *  rows without re-implementing the grouping. */
export interface RoundTrip {
  positionId: number;
  symbol: string;
  type: string; // "buy" | "sell"
  volume: number;
  entryPrice: number;
  entryTime: number;
  exitPrice: number;
  exitTime: number;
  profit: number;
  commission: number;
  swap: number;
  net: number;
}

export function groupDealsToRoundTrips(deals: TradingDeal[]): RoundTrip[] {
  // Bucket by position_id, skip non-trade deals (balance / credit).
  const byPos = new Map<number, TradingDeal[]>();
  for (const d of deals) {
    // type 2/3 = balance/credit (deposits, dividends) — not trades.
    if (d.type === 2 || d.type === 3) continue;
    if (!d.position_id) continue;
    const arr = byPos.get(d.position_id) ?? [];
    arr.push(d);
    byPos.set(d.position_id, arr);
  }

  const trips: RoundTrip[] = [];
  for (const [positionId, deals] of byPos.entries()) {
    deals.sort((a, b) => a.time - b.time);
    const entry = deals.find((d) => d.entry_label === "in");
    const exits = deals.filter(
      (d) => d.entry_label === "out" || d.entry_label === "out_by",
    );
    // Skip open trades that only have an "in" deal — they're still
    // running and show up in the Positions section instead.
    if (!entry || exits.length === 0) continue;
    const lastExit = exits[exits.length - 1];
    const profit = exits.reduce((acc, d) => acc + d.profit, 0);
    const commission = deals.reduce((acc, d) => acc + d.commission, 0);
    const swap = deals.reduce((acc, d) => acc + d.swap, 0);
    trips.push({
      positionId,
      symbol: entry.symbol,
      type: entry.type_label,
      volume: entry.volume,
      entryPrice: entry.price,
      entryTime: entry.time,
      exitPrice: lastExit.price,
      exitTime: lastExit.time,
      profit,
      commission,
      swap,
      net: profit + commission + swap,
    });
  }
  // Newest first.
  trips.sort((a, b) => b.exitTime - a.exitTime);
  return trips;
}

/** Modal listing the trading server open positions + pending orders + recent
 *  closed-trade history (last 30 days). The positions / orders
 *  sections are kept fresh by the 4s polling hook; history is
 *  fetched once when the modal opens (heavier query, doesn't need
 *  to be live). */
export function PositionsPanel({ open, onClose }: Props) {
  const dispatch = useAppDispatch();
  // Defence in depth: this modal is only reachable via the
  // Header's trading chip (owner-gated), but gating here too means
  // a guest who somehow ended up with `open: true` (stale state,
  // hand-dispatched action, etc) sees nothing — no account
  // numbers, no positions, no journal-relevant data.
  const canTrade = useCan(CAP.tradingRead);
  const account = useAppSelector((s) => s.trading.account);
  const positions = useAppSelector((s) => s.trading.positions);
  const orders = useAppSelector((s) => s.trading.orders);
  const history = useAppSelector((s) => s.trading.history);
  const historyStatus = useAppSelector((s) => s.trading.historyStatus);
  const connected = useAppSelector((s) => s.trading.connected);
  const lastPolledAt = useAppSelector((s) => s.trading.lastPolledAt);

  const [tab, setTab] = useState<Tab>("positions");

  // Inline row editing (SL/TP for positions; price/SL/TP for pending).
  // `editTicket` is the row currently in edit mode; `busyTicket` blocks a
  // row's buttons while its request is in flight.
  const [editTicket, setEditTicket] = useState<number | null>(null);
  const [draftPrice, setDraftPrice] = useState("");
  const [draftSl, setDraftSl] = useState("");
  const [draftTp, setDraftTp] = useState("");
  const [busyTicket, setBusyTicket] = useState<number | null>(null);

  const startEdit = (
    ticket: number,
    price: number | null,
    sl: number,
    tp: number,
  ) => {
    setEditTicket(ticket);
    setDraftPrice(price != null && price > 0 ? String(price) : "");
    setDraftSl(sl > 0 ? String(sl) : "");
    setDraftTp(tp > 0 ? String(tp) : "");
  };
  const cancelEdit = () => setEditTicket(null);

  /** Run an order action, toast the outcome, clear edit/busy state. */
  const runAction = async (
    ticket: number,
    fn: () => Promise<{ ok: boolean; human_msg: string }>,
    successTitle: string,
  ) => {
    setBusyTicket(ticket);
    try {
      const res = await fn();
      dispatch(
        res.ok
          ? pushToast({ kind: "success", title: successTitle })
          : pushToast({
              kind: "error",
              title: "Action failed",
              message: res.human_msg,
            }),
      );
      if (res.ok) setEditTicket(null);
    } catch (e) {
      dispatch(
        pushToast({
          kind: "error",
          title: "Action failed",
          message: e instanceof ApiError ? e.message : "Network error",
        }),
      );
    } finally {
      setBusyTicket(null);
    }
  };

  const closePosition = (ticket: number) =>
    runAction(
      ticket,
      () => api.tradingCloseOrder({ ticket }),
      `Closed #${ticket}`,
    );
  // Panic "close all" — market-closes every open position sequentially
  // (the backend serializes order_send under its poll lock anyway) and
  // summarizes the outcome in one toast.
  const [closingAll, setClosingAll] = useState(false);
  const closeAllPositions = async () => {
    const list = positions.slice();
    if (!list.length || closingAll) return;
    setClosingAll(true);
    let ok = 0;
    let failed = 0;
    let lastErr = "";
    for (const p of list) {
      try {
        const res = await api.tradingCloseOrder({ ticket: p.ticket });
        if (res.ok) ok++;
        else {
          failed++;
          lastErr = res.human_msg;
        }
      } catch (e) {
        failed++;
        lastErr = e instanceof ApiError ? e.message : "Network error";
      }
    }
    dispatch(
      failed === 0
        ? pushToast({
            kind: "success",
            title: `Closed ${ok} position${ok === 1 ? "" : "s"}`,
          })
        : pushToast({
            kind: ok ? "warning" : "error",
            title: `Closed ${ok}, ${failed} failed`,
            message: lastErr,
          }),
    );
    setClosingAll(false);
  };
  const savePositionSlTp = (ticket: number) =>
    runAction(
      ticket,
      () =>
        api.tradingModifyOrder({
          ticket,
          sl: parsePrice(draftSl) ?? 0,
          tp: parsePrice(draftTp) ?? 0,
        }),
      `Updated SL/TP on #${ticket}`,
    );
  const cancelPending = (ticket: number) =>
    runAction(
      ticket,
      () => api.tradingCancelOrder({ ticket }),
      `Cancelled #${ticket}`,
    );
  const savePendingModify = (ticket: number) =>
    runAction(
      ticket,
      () =>
        api.tradingModifyPending({
          ticket,
          price: parsePrice(draftPrice) ?? undefined,
          sl: parsePrice(draftSl) ?? 0,
          tp: parsePrice(draftTp) ?? 0,
        }),
      `Modified pending #${ticket}`,
    );

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Fetch history once when the modal opens (don't poll — it's
  // historical, won't change between two clicks on the chip).
  // Re-fetch each open so the user sees recently-closed trades.
  useEffect(() => {
    if (!open) return;
    dispatch(fetchTradingHistory({ days: 30 }));
  }, [open, dispatch]);

  const sortedPositions = useMemo(
    () => [...positions].sort((a, b) => b.time - a.time),
    [positions],
  );
  const sortedOrders = useMemo(
    () => [...orders].sort((a, b) => b.time_setup - a.time_setup),
    [orders],
  );
  const roundTrips = useMemo(
    () => groupDealsToRoundTrips(history.deals),
    [history.deals],
  );
  const totalOpenPnl = positions.reduce((acc, p) => acc + p.profit, 0);
  const totalHistoryPnl = roundTrips.reduce((acc, t) => acc + t.net, 0);
  const lastUpdated = lastPolledAt
    ? fmtClock(Math.floor(lastPolledAt / 1000))
    : "—";

  // Footer quick-trade targets the active chart's symbol at the shared
  // default volume, routed through the same risk-gated endpoint.
  const activeSymbol = useAppSelector(
    (s) => s.ui.byTab[s.tabs.activeTabId]?.symbol ?? "",
  );
  const tradeVolume = useAppSelector((s) => s.ui.tradeVolume);
  const [placing, setPlacing] = useState<null | "buy" | "sell">(null);
  const footerTrade = async (side: "buy" | "sell") => {
    if (!activeSymbol || !connected || placing) return;
    setPlacing(side);
    try {
      const outcome = await api.tradingPlaceOrder({
        symbol: activeSymbol,
        side,
        volume: tradeVolume,
      });
      if (outcome.kind === "filled") {
        dispatch(
          pushToast({
            kind: "success",
            title: `${side === "buy" ? "Buy" : "Sell"} ${tradeVolume} ${activeSymbol} @ ${outcome.result.price} filled`,
          }),
        );
      } else if (outcome.kind === "rejected") {
        dispatch(
          pushToast({
            kind: "error",
            title: "Order rejected",
            message: outcome.result.human_msg,
          }),
        );
      } else {
        dispatch(
          pushToast({
            kind: "warning",
            title: "Blocked by a risk control",
            message: outcome.message,
          }),
        );
      }
    } catch (e) {
      dispatch(
        pushToast({
          kind: "error",
          title: "Order failed",
          message: e instanceof ApiError ? e.message : "Network error",
        }),
      );
    } finally {
      setPlacing(null);
    }
  };

  if (!open || !canTrade) return null;

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Trading"
      >
        <header className={styles.header}>
          <span className={styles.title}>Trading</span>
          <span className={styles.live}>
            <span
              className={`${styles.liveDot} ${connected ? "" : styles.liveDotOff}`}
            />
            {connected ? "Live" : "Offline"} · {lastUpdated}
          </span>
          <div className={styles.acct}>
            {account ? (
              <>
                <span className={styles.badge}>#{account.login}</span>
                <span>{account.server}</span>
                <span className={styles.lev}>1:{account.leverage}</span>
              </>
            ) : null}
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                <path
                  d="M6 6l12 12M18 6L6 18"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </header>

        {account ? (
          <div className={styles.metrics}>
            <div className={`${styles.metric} ${styles.metricEquity}`}>
              <div className={styles.metricLbl}>Equity</div>
              <div className={styles.metricVal}>
                {money(account.equity)}{" "}
                <span className={styles.metricU}>{account.currency}</span>
              </div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLbl}>Balance</div>
              <div className={styles.metricVal}>{money(account.balance)}</div>
            </div>
            <div
              className={`${styles.metric} ${account.profit < 0 ? styles.metricAccentP : ""}`}
            >
              <div className={styles.metricLbl}>Floating P&amp;L</div>
              <div
                className={`${styles.metricVal} ${account.profit >= 0 ? styles.up : styles.down}`}
              >
                {account.profit >= 0 ? "+" : ""}
                {money(account.profit)}
              </div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLbl}>Free margin</div>
              <div className={styles.metricVal}>{money(account.margin_free)}</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricLbl}>Margin level</div>
              <div
                className={`${styles.metricVal} ${account.margin_level > 0 && account.margin_level < 200 ? styles.down : styles.up}`}
              >
                {account.margin_level > 0 ? (
                  <>
                    {account.margin_level.toFixed(0)}
                    <span className={styles.metricU}>%</span>
                  </>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "positions"}
            className={`${styles.tab} ${tab === "positions" ? styles.tabOn : ""}`}
            onClick={() => setTab("positions")}
          >
            Positions <span className={styles.tabN}>{positions.length}</span>
            {positions.length > 0 ? (
              <span
                className={`${styles.tabPl} ${totalOpenPnl >= 0 ? styles.up : styles.down}`}
              >
                {totalOpenPnl >= 0 ? "+" : ""}
                {totalOpenPnl.toFixed(2)}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "orders"}
            className={`${styles.tab} ${tab === "orders" ? styles.tabOn : ""}`}
            onClick={() => setTab("orders")}
          >
            Pending <span className={styles.tabN}>{orders.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "history"}
            className={`${styles.tab} ${tab === "history" ? styles.tabOn : ""}`}
            onClick={() => setTab("history")}
          >
            History · 30d <span className={styles.tabN}>{roundTrips.length}</span>
            {roundTrips.length > 0 ? (
              <span
                className={`${styles.tabPl} ${totalHistoryPnl >= 0 ? styles.up : styles.down}`}
              >
                {totalHistoryPnl >= 0 ? "+" : ""}
                {totalHistoryPnl.toFixed(2)}
              </span>
            ) : null}
          </button>
        </div>

        <div className={styles.body}>
          {!connected ? (
            <div className={styles.notConnected}>
              Trading server not connected.
            </div>
          ) : tab === "positions" ? (
            sortedPositions.length === 0 ? (
              <div className={styles.empty}>No open positions.</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Volume</th>
                    <th>Entry</th>
                    <th>Current</th>
                    <th>P&L</th>
                    <th>Swap</th>
                    <th>SL</th>
                    <th>TP</th>
                    <th>Opened</th>
                    <th>Ticket</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPositions.map((p) => {
                    const profitClass =
                      p.profit >= 0
                        ? styles.profitPositive
                        : styles.profitNegative;
                    const editing = editTicket === p.ticket;
                    const busy = busyTicket === p.ticket;
                    return (
                      <tr key={p.ticket}>
                        <td>{p.symbol}</td>
                        <td>
                          <span
                            className={
                              p.type === 0
                                ? styles.typeBuy
                                : styles.typeSell
                            }
                          >
                            {p.type_label}
                          </span>
                        </td>
                        <td>{p.volume.toFixed(2)}</td>
                        <td>{p.price_open.toFixed(3)}</td>
                        <td>{p.price_current.toFixed(3)}</td>
                        <td className={profitClass}>
                          {p.profit >= 0 ? "+" : ""}
                          {p.profit.toFixed(2)}
                        </td>
                        <td>{p.swap.toFixed(2)}</td>
                        <td>
                          {editing ? (
                            <input
                              className={styles.editInput}
                              type="number"
                              step="0.001"
                              placeholder="none"
                              value={draftSl}
                              onChange={(e) => setDraftSl(e.target.value)}
                            />
                          ) : p.sl > 0 ? (
                            p.sl.toFixed(3)
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {editing ? (
                            <input
                              className={styles.editInput}
                              type="number"
                              step="0.001"
                              placeholder="none"
                              value={draftTp}
                              onChange={(e) => setDraftTp(e.target.value)}
                            />
                          ) : p.tp > 0 ? (
                            p.tp.toFixed(3)
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{formatShort(p.time)}</td>
                        <td>{p.ticket}</td>
                        <td className={styles.actionsCell}>
                          {editing ? (
                            <>
                              <button
                                className={styles.actSave}
                                disabled={busy}
                                onClick={() => savePositionSlTp(p.ticket)}
                              >
                                {busy ? "…" : "Save"}
                              </button>
                              <button
                                className={styles.actGhost}
                                disabled={busy}
                                onClick={cancelEdit}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className={styles.actGhost}
                                disabled={busy}
                                onClick={() =>
                                  startEdit(p.ticket, null, p.sl, p.tp)
                                }
                              >
                                SL/TP
                              </button>
                              <button
                                className={styles.actDanger}
                                disabled={busy}
                                onClick={() => closePosition(p.ticket)}
                              >
                                {busy ? "…" : "Close"}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : tab === "orders" ? (
            sortedOrders.length === 0 ? (
              <div className={styles.empty}>No pending orders.</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Volume</th>
                    <th>Price</th>
                    <th>Current</th>
                    <th>SL</th>
                    <th>TP</th>
                    <th>Placed</th>
                    <th>Expires</th>
                    <th>Ticket</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOrders.map((o) => {
                    const isBuy = o.type_label.startsWith("buy");
                    const editing = editTicket === o.ticket;
                    const busy = busyTicket === o.ticket;
                    return (
                      <tr key={o.ticket}>
                        <td>{o.symbol}</td>
                        <td>
                          <span
                            className={
                              isBuy ? styles.typeBuy : styles.typeSell
                            }
                          >
                            {o.type_label}
                          </span>
                        </td>
                        <td>{o.volume_current.toFixed(2)}</td>
                        <td>
                          {editing ? (
                            <input
                              className={styles.editInput}
                              type="number"
                              step="0.001"
                              value={draftPrice}
                              onChange={(e) => setDraftPrice(e.target.value)}
                            />
                          ) : (
                            o.price_open.toFixed(3)
                          )}
                        </td>
                        <td>{o.price_current.toFixed(3)}</td>
                        <td>
                          {editing ? (
                            <input
                              className={styles.editInput}
                              type="number"
                              step="0.001"
                              placeholder="none"
                              value={draftSl}
                              onChange={(e) => setDraftSl(e.target.value)}
                            />
                          ) : o.sl > 0 ? (
                            o.sl.toFixed(3)
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {editing ? (
                            <input
                              className={styles.editInput}
                              type="number"
                              step="0.001"
                              placeholder="none"
                              value={draftTp}
                              onChange={(e) => setDraftTp(e.target.value)}
                            />
                          ) : o.tp > 0 ? (
                            o.tp.toFixed(3)
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>{formatShort(o.time_setup)}</td>
                        <td>
                          {o.expires_in != null ? (
                            fmtCountdown(o.expires_in)
                          ) : (
                            <span className={styles.muted}>GTC</span>
                          )}
                        </td>
                        <td>{o.ticket}</td>
                        <td className={styles.actionsCell}>
                          {editing ? (
                            <>
                              <button
                                className={styles.actSave}
                                disabled={busy}
                                onClick={() => savePendingModify(o.ticket)}
                              >
                                {busy ? "…" : "Save"}
                              </button>
                              <button
                                className={styles.actGhost}
                                disabled={busy}
                                onClick={cancelEdit}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className={styles.actGhost}
                                disabled={busy}
                                onClick={() =>
                                  startEdit(
                                    o.ticket,
                                    o.price_open,
                                    o.sl,
                                    o.tp,
                                  )
                                }
                              >
                                Edit
                              </button>
                              <button
                                className={styles.actDanger}
                                disabled={busy}
                                onClick={() => cancelPending(o.ticket)}
                              >
                                {busy ? "…" : "Cancel"}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : (
            // history tab
            historyStatus === "loading" ? (
              <div className={styles.empty}>Loading history…</div>
            ) : roundTrips.length === 0 ? (
              <div className={styles.empty}>
                No closed trades in the last 30 days.
              </div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Volume</th>
                    <th>Entry</th>
                    <th>Exit</th>
                    <th>P&L</th>
                    <th>Swap</th>
                    <th>Commission</th>
                    <th>Net</th>
                    <th>Opened</th>
                    <th>Closed</th>
                    <th>Position</th>
                  </tr>
                </thead>
                <tbody>
                  {roundTrips.map((t) => {
                    const netClass =
                      t.net >= 0
                        ? styles.profitPositive
                        : styles.profitNegative;
                    return (
                      <tr key={t.positionId}>
                        <td>{t.symbol}</td>
                        <td>
                          <span
                            className={
                              t.type === "buy"
                                ? styles.typeBuy
                                : styles.typeSell
                            }
                          >
                            {t.type}
                          </span>
                        </td>
                        <td>{t.volume.toFixed(2)}</td>
                        <td>{t.entryPrice.toFixed(3)}</td>
                        <td>{t.exitPrice.toFixed(3)}</td>
                        <td className={netClass}>
                          {t.profit >= 0 ? "+" : ""}
                          {t.profit.toFixed(2)}
                        </td>
                        <td>{t.swap.toFixed(2)}</td>
                        <td>{t.commission.toFixed(2)}</td>
                        <td className={netClass}>
                          {t.net >= 0 ? "+" : ""}
                          {t.net.toFixed(2)}
                        </td>
                        <td>{formatShort(t.entryTime)}</td>
                        <td>{formatShort(t.exitTime)}</td>
                        <td>{t.positionId}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
        </div>

        <div className={styles.foot}>
          <span className={styles.footK}>Total floating</span>
          <span
            className={`${styles.footTot} ${totalOpenPnl >= 0 ? styles.up : styles.down}`}
          >
            {totalOpenPnl >= 0 ? "+" : "−"}$
            {Math.abs(totalOpenPnl).toFixed(2)}
          </span>
          <div className={styles.footRight}>
            {positions.length > 0 ? (
              <button
                type="button"
                className={`${styles.qbtn} ${styles.qbtnClose}`}
                disabled={!connected || closingAll}
                onClick={() => void closeAllPositions()}
                title="Market-close every open position"
              >
                {closingAll ? "…" : `Close all (${positions.length})`}
              </button>
            ) : null}
            <button
              type="button"
              className={`${styles.qbtn} ${styles.qbtnBuy}`}
              disabled={!connected || !activeSymbol || placing !== null}
              onClick={() => footerTrade("buy")}
              title={
                activeSymbol
                  ? `Buy ${tradeVolume} ${activeSymbol} at market`
                  : "Open a chart first"
              }
            >
              {placing === "buy" ? "…" : `Buy ${activeSymbol}`.trim()}
            </button>
            <button
              type="button"
              className={`${styles.qbtn} ${styles.qbtnSell}`}
              disabled={!connected || !activeSymbol || placing !== null}
              onClick={() => footerTrade("sell")}
              title={
                activeSymbol
                  ? `Sell ${tradeVolume} ${activeSymbol} at market`
                  : "Open a chart first"
              }
            >
              {placing === "sell" ? "…" : `Sell ${activeSymbol}`.trim()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Trade times are MT5/UTC — render in the chart display zone. See chartTime.ts.
const formatShort = fmtShortDateTime;
